import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  Typography,
  Chip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Alert,
} from '@mui/material';
import { Company, ClientUser } from '@otw/models';
import { useUsers } from 'hooks/useUsers';
import { useCompanies, useCompany } from 'hooks/admin/useCompany';
import { apiClient } from '@/core/api/apiClient';
import { Controller, useForm } from 'react-hook-form';

interface CompanyManagementDialogProps {
  open: boolean;
  onClose: () => void;
  onCompanyCreated: (company: Company) => void;
  existingCompany?: Company | null;
}

interface CompanyFormData {
  name: string;
  user_ids: string[];
  notes?: string;
  website?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

export const CompanyManagementDialog: React.FC<CompanyManagementDialogProps> = ({
  open,
  onClose,
  onCompanyCreated,
  existingCompany,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<ClientUser[]>([]);
  const [selectedProfileByUserId, setSelectedProfileByUserId] = useState<Record<string, string>>({});
  const [businessProfilesByUserId, setBusinessProfilesByUserId] = useState<Record<string, Array<{ id: string; profile_name?: string }>>>({});
  const [isLoadingBusinessProfiles, setIsLoadingBusinessProfiles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const { users, isLoading: isLoadingUsers } = useUsers();
  
  // Use the companies hook for creating new companies
  const { createCompany } = useCompanies({
    onSuccess: () => {
      // Success - close dialog and let parent handle data refresh
      handleClose();
    },
    onError: (err) => {
      setError(err.message);
    },
    dontShowUpdates: true, // We'll handle success/error messages manually
  });

  // Only use the single company hook for updating existing companies when we have a company ID
  const { updateCompany } = useCompany(
    existingCompany?.id || '',
    {
      onSuccess: () => {
        // Success - close dialog and let parent handle data refresh
        handleClose();
      },
      onError: (err) => {
        setError(err.message);
      },
      dontShowUpdates: true,
      enabled: !!existingCompany?.id, // Only enable when we have an existing company
    }
  );
  
  // Filter to only client users - memoize to prevent unnecessary re-renders
  const clientUsers = useMemo(() => 
    users?.filter(user => user.role === 'client') as ClientUser[] || [], 
    [users]
  );

  // Determine mode based on whether existingCompany is provided
  const mode = existingCompany ? 'edit' : 'create';

  // Create default values - memoize to prevent re-creation on every render
  const defaultValues = useMemo(() => ({
    name: '',
    user_ids: [],
    notes: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
    },
  }), []);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CompanyFormData>({
    defaultValues,
  });

  // Watch user_ids to sync with selectedUsers
  const watchedUserIds = watch('user_ids');

  // Only initialize form when dialog opens or company changes
  useEffect(() => {
    if (open && !isInitialized) {
      if (mode === 'edit' && existingCompany) {
        // Populate form with existing company data
        const formData = {
          name: existingCompany.name,
          user_ids: existingCompany.user_ids || [],
          notes: existingCompany.notes || '',
          website: existingCompany.website || '',
          address: existingCompany.address || {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'US',
          },
        };
        
        reset(formData);
        
        // Set selected users based on existing company
        const companyUsers = clientUsers.filter(user => existingCompany.user_ids?.includes(user.id));
        setSelectedUsers(companyUsers);
        setSelectedProfileByUserId(
          Object.fromEntries(
            (existingCompany.user_ids || []).map(userId => [
              userId,
              (existingCompany.user_financing_profile_assignments || {})[userId] || 'main',
            ]),
          ),
        );
      } else {
        // Reset form for new company
        reset(defaultValues);
        setSelectedUsers([]);
        setSelectedProfileByUserId({});
        setBusinessProfilesByUserId({});
      }
      setError(null);
      setIsInitialized(true);
    }
  }, [open, isInitialized, mode, existingCompany, reset, defaultValues, clientUsers]);

  // Reset initialization flag when dialog closes
  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);

  // Sync selectedUsers when user_ids changes via form - but only after initialization
  useEffect(() => {
    if (isInitialized && clientUsers.length > 0) {
      const newSelectedUsers = clientUsers.filter(user => watchedUserIds.includes(user.id));
      if (JSON.stringify(newSelectedUsers) !== JSON.stringify(selectedUsers)) {
        setSelectedUsers(newSelectedUsers);
      }
    }
  }, [watchedUserIds, clientUsers, isInitialized, selectedUsers]);

  const handleUserSelection = (users: ClientUser[]) => {
    setSelectedUsers(users);
    const userIds = users.map(user => user.id);
    setValue('user_ids', userIds);
    setSelectedProfileByUserId(prev => {
      const next: Record<string, string> = {}
      users.forEach(user => {
        next[user.id] = prev[user.id] || (existingCompany?.user_financing_profile_assignments || {})[user.id] || 'main'
      })
      return next
    })
  };

  const handleUserProfileSelection = (userId: string, profileId: string) => {
    setSelectedProfileByUserId(prev => ({
      ...prev,
      [userId]: profileId,
    }))
  }

  useEffect(() => {
    const loadBusinessProfiles = async () => {
      if (!open || selectedUsers.length === 0) {
        setBusinessProfilesByUserId({})
        return
      }

      setIsLoadingBusinessProfiles(true)
      try {
        const responses = await Promise.all(
          selectedUsers.map(async user => {
            const response = await apiClient.portal.admin.getClientBusinessProfiles(user.id)
            return {
              userId: user.id,
              profiles: response?.data || [],
            }
          }),
        )

        const nextMap: Record<string, Array<{ id: string; profile_name?: string }>> = {}
        responses.forEach(({ userId, profiles }) => {
          nextMap[userId] = profiles
        })
        setBusinessProfilesByUserId(nextMap)
      } catch (err) {
        console.error('Failed to load business profile variants:', err)
      } finally {
        setIsLoadingBusinessProfiles(false)
      }
    }

    loadBusinessProfiles()
  }, [open, selectedUsers])

  const onSubmit = async (data: CompanyFormData) => {
    setError(null);

    try {
      // Basic validation
      if (!data.name.trim()) {
        throw new Error('Company name is required');
      }
      
      if (data.user_ids.length === 0) {
        throw new Error('At least one user must be selected');
      }

      // Helper function to check if address has any meaningful content
      const hasAddressContent = (addr: typeof data.address) => {
        if (!addr) return false;
        return !!(addr.street?.trim() || addr.city?.trim() || addr.state?.trim() || addr.zipCode?.trim());
      };

      // Helper function to clean address - only include if it has content
      const cleanAddress = (addr: typeof data.address) => {
        if (!hasAddressContent(addr)) return undefined;
        const cleaned = {
          street: addr!.street?.trim() || '',
          city: addr!.city?.trim() || '',
          state: addr!.state?.trim() || '',
          zipCode: addr!.zipCode?.trim() || '',
          country: addr!.country?.trim() || 'US',
        };
        // Only return if at least one field has content
        return (cleaned.street || cleaned.city || cleaned.state || cleaned.zipCode) ? cleaned : undefined;
      };

      // Helper function to clean optional string fields
      const cleanOptionalString = (value: string | undefined) => {
        const trimmed = value?.trim();
        return trimmed && trimmed.length > 0 ? trimmed : undefined;
      };

      if (mode === 'create') {
        // Create new company with all required fields and default values
        const companyData: any = {
          name: data.name.trim(),
          user_ids: data.user_ids,
          // Required fields with default values
          entity_structure: 'LLC',
          primary_contact_user_id: data.user_ids[0], // Use first selected user as primary contact
          is_active: true,
          user_financing_profile_assignments: Object.fromEntries(
            data.user_ids.map(userId => [userId, selectedProfileByUserId[userId] || 'main']),
          ),
        };

        // Only include optional fields if they have content
        const cleanedNotes = cleanOptionalString(data.notes);
        if (cleanedNotes) companyData.notes = cleanedNotes;

        const cleanedWebsite = cleanOptionalString(data.website);
        if (cleanedWebsite) companyData.website = cleanedWebsite;

        const cleanedAddress = cleanAddress(data.address);
        if (cleanedAddress) companyData.address = cleanedAddress;

        await createCompany({ data: companyData });
      } else if (existingCompany && updateCompany) {
        // Update existing company
        const updateData: Partial<Company> = {
          name: data.name.trim(),
          user_ids: data.user_ids,
          primary_contact_user_id: data.user_ids[0], // Update primary contact to first selected user
          user_financing_profile_assignments: Object.fromEntries(
            data.user_ids.map(userId => [
              userId,
              selectedProfileByUserId[userId] ||
                (existingCompany.user_financing_profile_assignments || {})[userId] ||
                'main',
            ]),
          ),
        };

        // Only include optional fields if they have content
        const cleanedNotes = cleanOptionalString(data.notes);
        if (cleanedNotes !== undefined) updateData.notes = cleanedNotes;

        const cleanedWebsite = cleanOptionalString(data.website);
        if (cleanedWebsite !== undefined) updateData.website = cleanedWebsite;

        const cleanedAddress = cleanAddress(data.address);
        if (cleanedAddress !== undefined) updateData.address = cleanedAddress;

        await updateCompany({ data: updateData });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedUsers([]);
      setSelectedProfileByUserId({});
      setBusinessProfilesByUserId({});
      setError(null);
      setIsInitialized(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create New Company' : 'Edit Company'}
      </DialogTitle>
      
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Company Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                {...register('name', { required: 'Company name is required' })}
                error={!!errors.name}
                helperText={errors.name?.message}
                required
              />
            </Grid>

            {/* User Selection */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={clientUsers}
                value={selectedUsers}
                onChange={(_, newValue) => handleUserSelection(newValue)}
                getOptionLabel={(user) => `${user.first_name} ${user.last_name} (${user.email})`}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((user, index) => (
                    <Chip
                      label={`${user.first_name} ${user.last_name}`}
                      {...getTagProps({ index })}
                      key={user.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Users"
                    placeholder="Choose users to add to this company"
                    helperText="Select all users that belong to this company"
                    error={!!errors.user_ids}
                    required
                  />
                )}
                loading={isLoadingUsers}
              />
              {errors.user_ids && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  At least one user must be selected
                </Typography>
              )}
            </Grid>

            {/* User Financing Profile Assignment */}
            {selectedUsers.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  User Financing Profile Assignment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose which profile each selected user should use for this company.
                </Typography>

                {isLoadingBusinessProfiles ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading profile variants...
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {selectedUsers.map(user => (
                      <Grid item xs={12} md={6} key={user.id}>
                        <TextField
                          select
                          fullWidth
                          label={`${user.first_name} ${user.last_name} profile`}
                          value={selectedProfileByUserId[user.id] || 'main'}
                          onChange={e => handleUserProfileSelection(user.id, e.target.value)}
                          helperText="Main is default. Select a business profile variant if needed."
                        >
                          <MenuItem value="main">Main Profile</MenuItem>
                          {(businessProfilesByUserId[user.id] || []).map(variant => (
                            <MenuItem key={variant.id} value={variant.id}>
                              {variant.profile_name || 'Business Profile'}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            )}

            {/* Website */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website (Optional)"
                {...register('website')}
                placeholder="https://www.company.com"
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Address (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                {...register('address.street')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                {...register('address.city')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                {...register('address.state')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                {...register('address.zipCode')}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                {...register('notes')}
                placeholder="Any additional notes about this company..."
              />
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit(onSubmit)}
          variant="contained" 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Company' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 